import { useCallback, useEffect, useMemo, useState } from 'react'

import { deliveryInstructionsFields } from 'DeliveryInstructions/DeliveryInstructionsConfig'
import { useDispatch, useSelector } from 'react-redux'

import type {
  LoggedOutBoxSummaryController,
  LoggedOutBoxSummaryPortionSize,
} from '@features/box-summary'

import { actionTypes } from 'actions/actionTypes'
import {
  basketChosenAddressChange,
  basketIsShowDetailsViewFirstChange,
  basketNumPortionChange,
  basketPostcodeClear,
  basketSlotClear,
} from 'actions/basket'
import {
  trackBoxSizeSelectionChange,
  trackDeliveryInstructionSelection,
  trackDeliveryOptionsShown,
} from 'actions/boxSummary'
import { setTempDeliveryOptions } from 'actions/deliveries'
import {
  saveDeliveryDetailsInstruction,
  saveDeliveryDetailsInstructionCustom,
} from 'actions/deliveryDetails'
import tempActions from 'actions/temp'
import { safePlaceDropDownSelected } from 'actions/trackingKeys'
import { useAvailableBoxSizes } from 'hooks/basket/useAvailableBoxSizes'
import { useBasketRecipes } from 'hooks/basket/useBasketRecipes'
import { useBasket } from 'routes/Menu/domains/basket'
import { useGetCurrentBoxSummaryView } from 'routes/Menu/hooks/useGetCurrentBoxSummaryView'

import { legacyViewToTreatmentStep } from './legacyViewToTreatmentStep'
import type { BoxSummaryTreatmentProps } from './props'

const DEFAULT_PORTION_SIZE: LoggedOutBoxSummaryPortionSize = 2
const PORTION_SIZES: LoggedOutBoxSummaryPortionSize[] = [1, 2, 3, 4, 5]
const POSTCODE_CONSTRAINTS = {
  minLength: 5,
  maxLength: 8,
}
const CUSTOM_DELIVERY_INSTRUCTION_CONSTRAINTS = {
  maxLength: 32,
}
const deliveryInstructionOptions = deliveryInstructionsFields.leaveBoxOptions.map((option) => ({
  value: option,
  label: option,
  requiresCustomValue: deliveryInstructionsFields.leaveBoxOptionsWithMessage.includes(option),
}))

const isPortionSize = (portionSize: number): portionSize is LoggedOutBoxSummaryPortionSize =>
  PORTION_SIZES.includes(portionSize as LoggedOutBoxSummaryPortionSize)

const getPortionSizeOrDefault = (portionSize: number): LoggedOutBoxSummaryPortionSize =>
  isPortionSize(portionSize) ? portionSize : DEFAULT_PORTION_SIZE

const isDeliveryInstructionOption = (instruction: string) =>
  deliveryInstructionsFields.leaveBoxOptions.includes(instruction)

const trackSafePlaceDropdownSelection = (selection: string) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: safePlaceDropDownSelected,
    selection,
  },
})

export const useLoggedOutBoxSummaryController = ({
  showDetails,
  boxDetailsVisibilityChange,
  basketIsPortionSizeViewVisibleChange,
  basketRestorePreviousValues,
  boxSummaryNext,
  restoreFocusAfterClose,
  numRecipes,
  numPortions,
}: BoxSummaryTreatmentProps): LoggedOutBoxSummaryController => {
  const dispatch = useDispatch<any>()
  const { removeRecipe } = useBasket()
  const { basketRecipes } = useBasketRecipes()
  const savedAddresses = useSelector((state: any) => state.user?.get('shippingAddresses'))
  const { availableBoxSizes, isLoading: arePortionSizesLoading } = useAvailableBoxSizes()
  const [selectedPortionSize, setSelectedPortionSize] = useState<LoggedOutBoxSummaryPortionSize>(
    getPortionSizeOrDefault(numPortions),
  )
  const currentView = useGetCurrentBoxSummaryView()
  const step = legacyViewToTreatmentStep(currentView)
  const availablePortionSizes = useMemo(
    () => availableBoxSizes?.filter(isPortionSize) ?? [],
    [availableBoxSizes],
  )
  const nullRecipeCoreIds = useMemo(
    () => basketRecipes.filter(({ recipe }) => recipe === null).map(({ coreId }) => coreId),
    [basketRecipes],
  )
  const canConfirmPortionSize =
    !arePortionSizesLoading && availablePortionSizes.includes(selectedPortionSize)

  useEffect(() => {
    setSelectedPortionSize(getPortionSizeOrDefault(numPortions))
  }, [numPortions])

  const onClose = useCallback(() => {
    basketIsPortionSizeViewVisibleChange(false)
    boxDetailsVisibilityChange(false)
    basketRestorePreviousValues()

    setTimeout(() => {
      restoreFocusAfterClose()
    }, 0)
  }, [
    basketIsPortionSizeViewVisibleChange,
    basketRestorePreviousValues,
    boxDetailsVisibilityChange,
    restoreFocusAfterClose,
  ])

  const actions = useMemo(
    () => ({
      onClose,
      onPostcodeChange: (postcode: string) => {
        dispatch(tempActions.temp('postcode', postcode))
      },
      onPostcodeAddressSelect: (addressId: string) => {
        const selectedAddress = savedAddresses?.find(
          (address: any) => address.get('id') === addressId,
        )

        if (selectedAddress) {
          dispatch(basketChosenAddressChange(selectedAddress))
          dispatch(tempActions.temp('postcode', selectedAddress.get('postcode')))
        }
      },
      onPostcodeContinue: boxSummaryNext,
      onDeliveryPostcodeEdit: () => {
        dispatch(basketPostcodeClear())
        dispatch(trackDeliveryOptionsShown())
      },
      onDeliveryDateSelect: (date: string) => {
        dispatch(setTempDeliveryOptions(date, ''))
      },
      onDeliverySlotSelect: (slotId: string) => {
        dispatch(tempActions.temp('slotId', slotId))
      },
      onDeliveryInstructionChange: (instruction: string) => {
        if (!isDeliveryInstructionOption(instruction)) {
          return
        }

        dispatch(saveDeliveryDetailsInstruction(instruction))
        dispatch(trackSafePlaceDropdownSelection(instruction))

        if (!deliveryInstructionsFields.leaveBoxOptionsWithMessage.includes(instruction)) {
          dispatch(saveDeliveryDetailsInstructionCustom(''))
        }
      },
      onDeliveryInstructionCustomChange: (instruction: string) => {
        dispatch(saveDeliveryDetailsInstructionCustom(instruction))
      },
      onDeliveryContinue: () => {
        dispatch(trackDeliveryInstructionSelection())
        boxSummaryNext()
      },
      onPortionSizeSelect: (portionSize: LoggedOutBoxSummaryPortionSize) => {
        if (!availablePortionSizes.includes(portionSize)) {
          return
        }

        setSelectedPortionSize(portionSize)
        dispatch(trackBoxSizeSelectionChange(portionSize, 'box_summary'))
      },
      onPortionSizeConfirm: () => {
        if (!canConfirmPortionSize) {
          return
        }

        nullRecipeCoreIds.forEach((coreId) => {
          removeRecipe(coreId)
        })
        dispatch(basketNumPortionChange(selectedPortionSize, 'box_summary'))
        basketIsPortionSizeViewVisibleChange(false)
      },
      onSummaryEditPostcode: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        dispatch(basketPostcodeClear())
        dispatch(trackDeliveryOptionsShown())
      },
      onSummaryEditDelivery: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        dispatch(basketSlotClear())
      },
      onSummaryEditPortionSize: () => {
        dispatch(basketIsShowDetailsViewFirstChange(false))
        basketIsPortionSizeViewVisibleChange(true)
      },
      onCheckout: () => undefined,
    }),
    [
      basketIsPortionSizeViewVisibleChange,
      availablePortionSizes,
      boxSummaryNext,
      canConfirmPortionSize,
      dispatch,
      nullRecipeCoreIds,
      onClose,
      removeRecipe,
      savedAddresses,
      selectedPortionSize,
    ],
  )

  return {
    isOpen: showDetails,
    step,
    views: {
      postcode: {
        title: 'Enter your postcode',
        postcode: '',
        constraints: POSTCODE_CONSTRAINTS,
        savedAddresses: [],
        selectedAddressId: null,
        validationMessage: null,
        continueCta: {
          label: 'Continue',
          isDisabled: true,
          isPending: false,
        },
      },
      delivery: {
        title: 'Choose delivery',
        postcodeLabel: '',
        selectedDateId: null,
        dateLabel: null,
        availableDates: [],
        selectedSlotId: null,
        slot: null,
        availableSlots: [],
        instructions: {
          value: '',
          customValue: '',
          options: deliveryInstructionOptions,
          placeholder: 'Add delivery instructions',
          customConstraints: CUSTOM_DELIVERY_INSTRUCTION_CONSTRAINTS,
          validationMessage: null,
          isRequired: true,
          isValid: true,
        },
        continueCta: {
          label: 'Continue to box size',
          isDisabled: true,
          isPending: false,
        },
      },
      portionSize: {
        title: 'Choose your box size',
        selectedPortionSize,
        availablePortionSizes: availablePortionSizes.map((portionSize) => ({
          value: portionSize,
          label: `${portionSize} people`,
          isSelected: portionSize === selectedPortionSize,
          isDisabled: arePortionSizesLoading,
        })),
        warningMessage: null,
        confirmCta: {
          label: 'Confirm box size',
          isDisabled: !canConfirmPortionSize,
          isPending: arePortionSizesLoading,
        },
      },
      summary: {
        title: 'Your box summary',
        recipeCount: numRecipes,
        minRecipeCount: 0,
        maxRecipeCount: numRecipes,
        portionSize: getPortionSizeOrDefault(numPortions),
        deliveryLabel: null,
        totalPrice: null,
        checkoutCta: {
          label: 'Continue to checkout',
          isDisabled: true,
          isPending: false,
        },
      },
    },
    actions,
  }
}
